import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Database, Download, RotateCcw, Plus, Loader2, AlertTriangle, CheckCircle, HardDrive, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import AdminLayout from './AdminLayout';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminBackups() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [message, setMessage] = useState(null);

  const token = localStorage.getItem('token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchBackups();
  }, []);

  const fetchBackups = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/backups`, authHeaders);
      setBackups(res.data.backups || []);
    } catch (err) {
      console.error('Failed to fetch backups:', err);
      setMessage({ type: 'error', text: 'Failed to load backups' });
    } finally {
      setLoading(false);
    }
  };

  const createBackup = async () => {
    setCreating(true);
    setMessage(null);
    try {
      await axios.post(`${API}/api/admin/backup`, {}, authHeaders);
      setMessage({ type: 'success', text: 'Backup created successfully!' });
      fetchBackups();
    } catch (err) {
      console.error('Backup failed:', err);
      setMessage({ type: 'error', text: 'Failed to create backup' });
    } finally {
      setCreating(false);
    }
  };

  const restoreBackup = async (filename) => {
    setRestoring(filename);
    setMessage(null);
    try {
      await axios.post(`${API}/api/admin/backup/restore/${filename}`, {}, authHeaders);
      setMessage({ type: 'success', text: `Database restored from ${filename}` });
      setConfirmRestore(null);
    } catch (err) {
      console.error('Restore failed:', err);
      setMessage({ type: 'error', text: 'Failed to restore backup' });
    } finally {
      setRestoring(null);
    }
  };

  const deleteBackup = async (filename) => {
    setMessage(null);
    try {
      await axios.delete(`${API}/api/admin/backup/${filename}`, authHeaders);
      setMessage({ type: 'success', text: `Backup ${filename} deleted` });
      setConfirmDelete(null);
      fetchBackups();
    } catch (err) {
      console.error('Delete failed:', err);
      setMessage({ type: 'error', text: 'Failed to delete backup' });
    }
  };

  return (
    <AdminLayout>
    <div className="space-y-6" data-testid="admin-backups-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Database Backups</h1>
          <p className="text-zinc-400 mt-1">Manage database backups and restore points</p>
        </div>
        <Button 
          onClick={createBackup} 
          disabled={creating}
          className="bg-green-600 hover:bg-green-700"
          data-testid="create-backup-btn"
        >
          {creating ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
          ) : (
            <><Plus className="w-4 h-4 mr-2" /> Create Backup</>
          )}
        </Button>
      </div>

      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${
          message.type === 'success' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Database className="w-5 h-5 text-green-500" />
            Available Backups
          </CardTitle>
          <CardDescription>
            Backups are created automatically every 6 hours. The last 10 backups are retained.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-green-500" />
            </div>
          ) : backups.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <HardDrive className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No backups found</p>
              <p className="text-sm mt-1">Create your first backup using the button above</p>
            </div>
          ) : (
            <div className="space-y-3">
              {backups.map((backup, idx) => (
                <div 
                  key={backup.filename}
                  className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg border border-zinc-700/50 hover:border-zinc-600 transition-colors"
                  data-testid={`backup-item-${idx}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      backup.is_manual ? 'bg-blue-500/20' : 'bg-green-500/20'
                    }`}>
                      <Database className={`w-5 h-5 ${backup.is_manual ? 'text-blue-500' : 'text-green-500'}`} />
                    </div>
                    <div>
                      <p className="text-white font-medium">{backup.filename}</p>
                      <p className="text-zinc-500 text-sm">
                        {backup.size_mb} MB • {backup.created}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {backup.is_manual && (
                      <span className="px-2 py-1 text-xs bg-blue-500/20 text-blue-400 rounded border border-blue-500/30">Manual</span>
                    )}
                    {idx === 0 && (
                      <span className="px-2 py-1 text-xs bg-green-500/20 text-green-400 rounded">Latest</span>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setConfirmRestore(backup.filename)}
                      disabled={restoring === backup.filename}
                      className="border-zinc-700 hover:bg-zinc-800"
                      data-testid={`restore-btn-${idx}`}
                    >
                      {restoring === backup.filename ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <><RotateCcw className="w-4 h-4 mr-1" /> Restore</>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setConfirmDelete(backup.filename)}
                      className="border-zinc-700 hover:bg-red-900/30 hover:border-red-700 hover:text-red-400"
                      data-testid={`delete-btn-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white text-lg">Backup Information</CardTitle>
        </CardHeader>
        <CardContent className="text-zinc-400 space-y-2 text-sm">
          <p><strong className="text-zinc-300">Automatic Backups:</strong> Run every 6 hours via cron job (last 10 retained)</p>
          <p><strong className="text-zinc-300">Manual Backups:</strong> Created via "Create Backup" button - <span className="text-blue-400">never auto-deleted</span></p>
          <p><strong className="text-zinc-300">Location:</strong> Backups are stored in /app/backups/ as compressed tar.gz archives</p>
          <p><strong className="text-zinc-300">What's Included:</strong> All MongoDB collections (orders, customers, products, invoices, etc.)</p>
          <p className="text-amber-400 mt-4">
            <AlertTriangle className="w-4 h-4 inline mr-1" />
            <strong>Warning:</strong> Restoring a backup will replace ALL current data with the backup's data. This cannot be undone.
          </p>
        </CardContent>
      </Card>

      {/* Restore Confirmation Dialog */}
      <AlertDialog open={!!confirmRestore} onOpenChange={() => setConfirmRestore(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Restore Database Backup?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will replace ALL current data with the data from:
              <br />
              <strong className="text-white">{confirmRestore}</strong>
              <br /><br />
              <span className="text-amber-400">This action cannot be undone. Consider creating a backup first.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => restoreBackup(confirmRestore)}
              className="bg-red-600 hover:bg-red-700"
            >
              Yes, Restore Backup
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Backup?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to permanently delete this backup?
              <br />
              <strong className="text-white">{confirmDelete}</strong>
              <br /><br />
              <span className="text-amber-400">This cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => deleteBackup(confirmDelete)}
              className="bg-red-600 hover:bg-red-700"
            >
              Yes, Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </AdminLayout>
  );
}
